import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import compose from 'compose-function';

import withData from 'components/withData';
import { getMutate } from 'utils/api';
import { white, brandColors } from 'styles/colors';

import { SettingsContext } from './';

const CONFIG_ID = 'settings';
const RESPONSE_PROP_TYPE = PropTypes.shape({
	loading: PropTypes.bool,
	data: PropTypes.any,
	refetch: PropTypes.func,
});
const DEFAULT_HIDE_DECIMALS = false;
const DEFAULT_COMPANY_NAME = 'Acme Inc.';
const DEFAULT_APP_TITLE = 'Procurement - Indirect';
const DEFAULT_BACKGROUND_COLOR = brandColors.green;
const DEFAULT_FOREGROUND_COLOR = white;
const DEFAULT_THRESHOLD_1 = 2;
const DEFAULT_THRESHOLD_2 = 5;

class Provider extends React.Component {
	static propTypes = {
		defaultCurrency: PropTypes.string.isRequired,
		defaultCurrencyType: PropTypes.string.isRequired,
		children: PropTypes.node.isRequired,
		configResponse: RESPONSE_PROP_TYPE,
	};

	state = {
		customFilters: [],
		opportunityYear: null,
		hideDecimals: DEFAULT_HIDE_DECIMALS,
		companyName: DEFAULT_COMPANY_NAME,
		appTitle: DEFAULT_APP_TITLE,
		backgroundColor: DEFAULT_BACKGROUND_COLOR,
		foregroundColor: DEFAULT_FOREGROUND_COLOR,
		threshold1: DEFAULT_THRESHOLD_1,
		threshold2: DEFAULT_THRESHOLD_2,

		refetchSettingsConfig: () => this.props.configResponse.refetch(),
		refetchCompanies: () => this.props.companiesListResponse.refetch(),
		refetchCompaniesMetadata: () =>
			this.props.companiesMetadataResponse.refetch(),
		refetchCustomFilters: () => this.props.customFiltersResponse.refetch(),

		updateSettingsConfig: config => {
			// Get previous value in case it needs to be reverted
			const beforeMutationConfig = {
				opportunityYear: this.state.opportunityYear,
				hideDecimals: this.state.hideDecimals,
				companyName: this.state.companyName,
				appTitle: this.state.appTitle,
				backgroundColor: this.state.backgroundColor,
				foregroundColor: this.state.foregroundColor,
				threshold1: this.state.threshold1,
				threshold2: this.state.threshold2,
			};
			const { customFilters: beforeMutationCustomFilters } = this.state;
			// Optimistic update
			this.setState(config);
			// Mutate
			// Save custom filters mutate
			const customFiltersMutate = getMutate('/spend/filters/custom', {
				method: 'PUT',
			});
			const configMutate = getMutate('/config', { method: 'POST' });
			const body = {
				...beforeMutationConfig,
				...config,
				id: CONFIG_ID,
			};

			return Promise.all([
				config.customFilters
					? customFiltersMutate(
							config.customFilters.map(({ name, isActive, label }) => ({
								name,
								isActive,
								label,
							}))
					  )
					: Promise.resolve({}),
				configMutate(body),
			]).then(([customFiltersMutateResponse, configMutateResponse]) => {
				if (customFiltersMutateResponse.ok) {
					// Validate optimistic update with server update
					this.state.refetchCustomFilters();
				} else {
					// Reverse optimistic update
					this.setState({ customFilters: beforeMutationCustomFilters });
				}

				if (configMutateResponse.ok) {
					this.state.refetchSettingsConfig();
					this.state.refetchCompanies();
				} else {
					this.setState(beforeMutationConfig);
				}

				return [customFiltersMutateResponse, configMutateResponse];
			});
		},
	};

	componentWillReceiveProps(nextProps) {
		const {
			configResponse,
			customFiltersResponse,
			companiesListResponse,
			companiesMetadataResponse,
		} = nextProps;

		// Define `customFilters` in state
		if (
			customFiltersResponse.success &&
			customFiltersResponse.success !== this.props.customFiltersResponse.success
		) {
			const responseData = customFiltersResponse.data || [];
			const customFilters = responseData.map(({ values, ...rest }) => ({
				...rest,
				values: values.sort(
					(a, b) =>
						a.value && b.value
							? a.value
									.trim()
									.localeCompare(b.value.trim(), 'en', { sensitivity: 'base' })
							: 0
				),
			}));

			this.setState({ customFilters });
		}

		// Define companiesList state
		if (
			companiesListResponse.success &&
			companiesListResponse.success !== this.props.companiesListResponse.success
		) {
			const companiesList = companiesListResponse.data || [];
			companiesList.sort();

			this.setState({ companiesList });
		}

		if (
			companiesMetadataResponse.success &&
			companiesMetadataResponse.success !==
				this.props.companiesMetadataResponse.success
		) {
			const companiesMetadata = companiesMetadataResponse.data || {};

			this.setState({ companiesMetadata });
		}

		if (
			configResponse.error &&
			configResponse.error !== this.props.configResponse.error &&
			get(configResponse, 'response.status') === 404
		) {
			this.state.updateSettingsConfig({});
		}

		// Define `opportunityYear` and `hideDecimals` in state
		if (
			configResponse.success &&
			configResponse.success !== this.props.configResponse.success
		) {
			this.state.refetchCompanies();
			const opportunityYear = get(configResponse, 'data.opportunityYear', null);

			const hideDecimals = get(
				configResponse,
				'data.hideDecimals',
				DEFAULT_HIDE_DECIMALS
			);

			const companyName = get(
				configResponse,
				'data.companyName',
				DEFAULT_COMPANY_NAME
			);

			const appTitle = get(configResponse, 'data.appTitle', DEFAULT_APP_TITLE);

			const backgroundColor = get(
				configResponse,
				'data.backgroundColor',
				DEFAULT_BACKGROUND_COLOR
			);

			const foregroundColor = get(
				configResponse,
				'data.foregroundColor',
				DEFAULT_FOREGROUND_COLOR
			);

			const threshold1 = get(
				configResponse,
				'data.threshold1',
				DEFAULT_THRESHOLD_1
			);

			const threshold2 = get(
				configResponse,
				'data.threshold2',
				DEFAULT_THRESHOLD_2
			);

			this.setState({
				opportunityYear,
				hideDecimals,
				companyName,
				appTitle,
				backgroundColor,
				foregroundColor,
				threshold1,
				threshold2,
			});
		}
	}

	render() {
		return (
			<SettingsContext.Provider value={this.state}>
				{this.props.children}
			</SettingsContext.Provider>
		);
	}
}

export const SettingsContextProvider = compose(
	withData(`/spend/filters/custom`, { name: 'customFiltersResponse' }),
	withData(`/spend/data/companies`, { name: 'companiesListResponse' }),
	withData(`/config/${CONFIG_ID}`, { name: 'configResponse' }),
	withData(`/companiesmetadata`, { name: 'companiesMetadataResponse' })
	// TOOD: add companies here
)(Provider);
